import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDuration } from '../lib/duration';
import { formatBucketKey, formatINR, MARK } from '../lib/format';

const AXIS = { fontSize: 11, fill: MARK.axis } as const;

function TooltipCard({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="card px-3 py-2 text-[12px]" style={{ boxShadow: 'var(--shadow-raised)' }}>
      <p className="font-semibold text-[var(--color-ink)]">{title}</p>
      {lines.map((line) => (
        <p key={line} className="nums mt-0.5 text-[var(--color-ink-soft)]">
          {line}
        </p>
      ))}
    </div>
  );
}

interface Point {
  key: string;
  minutes: number;
  amount_inr: number;
}

/** Hours worked per bucket. One series, so no legend is needed — the title names it. */
export function HoursChart({ data, height = 200 }: { data: Point[]; height?: number }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: -18 }} barCategoryGap="22%">
        <CartesianGrid stroke={MARK.grid} vertical={false} />
        <XAxis
          dataKey="key"
          tickFormatter={formatBucketKey}
          tick={AXIS}
          tickLine={false}
          axisLine={{ stroke: MARK.grid }}
          interval="preserveStartEnd"
          minTickGap={14}
        />
        <YAxis
          tick={AXIS}
          tickLine={false}
          axisLine={false}
          tickFormatter={(m: number) => String(Math.round(m / 60))}
          width={44}
        />
        <Tooltip
          cursor={{ fill: 'rgba(59,98,196,0.07)' }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipCard
                title={formatBucketKey(String(payload[0]!.payload.key))}
                lines={[
                  `${formatDuration(payload[0]!.payload.minutes)} logged`,
                  formatINR(payload[0]!.payload.amount_inr),
                ]}
              />
            ) : null
          }
        />
        {/* 4px rounded data-end, anchored to the baseline. */}
        <Bar dataKey="minutes" fill={MARK.hours} radius={[4, 4, 0, 0]} maxBarSize={38} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Compensation over time. A single coral line at 2px, with 8px markers. */
export function MoneyChart({ data, height = 200 }: { data: Point[]; height?: number }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 10, bottom: 0, left: -6 }}>
        <CartesianGrid stroke={MARK.grid} vertical={false} />
        <XAxis
          dataKey="key"
          tickFormatter={formatBucketKey}
          tick={AXIS}
          tickLine={false}
          axisLine={{ stroke: MARK.grid }}
        />
        <YAxis
          tick={AXIS}
          tickLine={false}
          axisLine={false}
          width={58}
          tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
        />
        <Tooltip
          cursor={{ stroke: MARK.grid, strokeWidth: 1 }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipCard
                title={formatBucketKey(String(payload[0]!.payload.key))}
                lines={[
                  formatINR(payload[0]!.payload.amount_inr),
                  `${formatDuration(payload[0]!.payload.minutes)} logged`,
                ]}
              />
            ) : null
          }
        />
        <Line
          type="monotone"
          dataKey="amount_inr"
          stroke={MARK.money}
          strokeWidth={2}
          dot={{ r: 4, fill: '#fff', stroke: MARK.money, strokeWidth: 2 }}
          activeDot={{ r: 5.5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export interface BreakdownItem {
  label: string;
  minutes: number;
  color?: string;
  meta?: string;
}

/**
 * Horizontal magnitude comparison. Every bar is directly labelled, so identity
 * never rests on colour — which is also why per-project colours are safe here.
 */
export function Breakdown({ items, accent }: { items: BreakdownItem[]; accent: string }) {
  const max = Math.max(...items.map((i) => i.minutes), 1);

  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item.label}>
          <div className="flex items-baseline justify-between gap-3 text-[13px]">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: item.color ?? accent }}
                aria-hidden
              />
              <span className="truncate">{item.label}</span>
            </span>
            <span className="nums shrink-0 font-semibold">
              {formatDuration(item.minutes)}
              {item.meta && (
                <span className="ml-2 font-normal text-[var(--color-ink-muted)]">{item.meta}</span>
              )}
            </span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-[var(--radius-pill)] bg-[var(--color-cyan-100)]">
            <div
              className="h-full rounded-[var(--radius-pill)]"
              style={{ width: `${(item.minutes / max) * 100}%`, background: item.color ?? accent }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
